
import { useState, useEffect, useMemo, useCallback } from 'react';
import { CustomerIndexItemDto } from '@techgitter/contracts';
import { getCustomerIndexCached, invalidateCustomerIndexCache } from '../../../services/customerIndexCacheService';

const DEBUG_CUSTOMER_LOOKUP = false;

interface CustomerIndexSearchItem extends CustomerIndexItemDto {
  searchHaystack: string;
  phoneTokens: string[];
}

let cachedSearchIndex: CustomerIndexSearchItem[] | null = null;

function buildSearchIndex(items: CustomerIndexItemDto[]): CustomerIndexSearchItem[] {
  return items.map(item => {
    const parts: string[] = [
      item.displayName,
      item.fullName,
      item.businessName,
      item.alias,
      item.email,
      item.domains,
    ].filter(Boolean).map(s => s!.toLowerCase());

    const searchHaystack = parts.join(' ');

    const phoneTokens: string[] = [];
    if (item.phoneNormalized) phoneTokens.push(item.phoneNormalized);
    if (item.mobileNormalized) phoneTokens.push(item.mobileNormalized);
    if (item.phone) {
      const digits = item.phone.replace(/\D/g, '');
      if (digits && !phoneTokens.includes(digits)) phoneTokens.push(digits);
    }
    if (item.mobile) {
      const digits = item.mobile.replace(/\D/g, '');
      if (digits && !phoneTokens.includes(digits)) phoneTokens.push(digits);
    }

    return {
      ...item,
      searchHaystack,
      phoneTokens,
    };
  });
}

function scoreMatch(item: CustomerIndexSearchItem, queryLower: string, queryDigits: string): number {
  const displayNameLower = item.displayName.toLowerCase();

  if (displayNameLower === queryLower) return 1000;
  if (displayNameLower.startsWith(queryLower)) return 900;
  if (displayNameLower.includes(queryLower)) return 800;

  if (queryDigits && item.phoneTokens.some((token: string) => token.includes(queryDigits))) {
    return 700;
  }

  if (item.searchHaystack.includes(queryLower)) return 600;

  return 0;
}

function filterAndSortResults(searchIndex: CustomerIndexSearchItem[], query: string): CustomerIndexItemDto[] {
  if (!query.trim()) {
    return searchIndex.slice(0, 50);
  }

  const queryLower = query.toLowerCase().trim();
  const queryDigits = query.replace(/\D/g, '');

  const matches = searchIndex
    .map(item => ({
      item,
      score: scoreMatch(item, queryLower, queryDigits),
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (a.item.customerType !== b.item.customerType) {
        return a.item.customerType === 'Business' ? -1 : 1;
      }
      if (a.score !== b.score) {
        return b.score - a.score;
      }
      return a.item.displayName.localeCompare(b.item.displayName);
    })
    .slice(0, 100)
    .map(({ item }) => item);

  return matches;
}

export function useCustomerLookup() {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [generatedAtUtc, setGeneratedAtUtc] = useState<string | null>(null);

  const loadIndex = useCallback(async () => {
    if (cachedSearchIndex) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const startTime = DEBUG_CUSTOMER_LOOKUP ? performance.now() : 0;

    try {
      const result = await getCustomerIndexCached();

      if (DEBUG_CUSTOMER_LOOKUP) {
        const loadTime = performance.now() - startTime;
        console.log(`[CustomerLookup] Loaded ${result.items.length} items in ${loadTime.toFixed(2)}ms`);
      }

      cachedSearchIndex = buildSearchIndex(result.items);
      setGeneratedAtUtc(result.generatedAtUtc);

      if (DEBUG_CUSTOMER_LOOKUP) {
        const totalTime = performance.now() - startTime;
        console.log(`[CustomerLookup] Index built in ${totalTime.toFixed(2)}ms`);
      }
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return;
      }
      const errorMessage = err.message || 'Failed to load customer index';
      setError(errorMessage);
      console.error('[CustomerLookup] Error loading index:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIndex();
  }, [loadIndex]);

  const results = useMemo(() => {
    if (!cachedSearchIndex) return [];

    const startTime = DEBUG_CUSTOMER_LOOKUP ? performance.now() : 0;
    const filtered = filterAndSortResults(cachedSearchIndex, query);

    if (DEBUG_CUSTOMER_LOOKUP && query) {
      const filterTime = performance.now() - startTime;
      console.log(`[CustomerLookup] Filtered to ${filtered.length} results in ${filterTime.toFixed(2)}ms for query: "${query}"`);
    }

    return filtered;
  }, [query]);

  const retry = useCallback(() => {
    cachedSearchIndex = null;
    invalidateCustomerIndexCache();
    loadIndex();
  }, [loadIndex]);

  const selectCustomer = useCallback((customer: CustomerIndexItemDto | null) => {
    setSelectedCustomer(customer);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedCustomer(null);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    retry,
    selectedCustomer,
    selectCustomer,
    clearSelection,
    generatedAtUtc,
  };
}
